import { useEffect, useRef } from 'react';
import { useLocation, useMatches } from 'react-router';

import { isIdentityReady, onIdentityReady } from './identityReady';
import { useAnalytics } from './useAnalytics';

// Routes set `handle: { requiresIdentity: true }` (the app shell does) to opt
// their `page_viewed` into the identity gate below.
type PageTrackingRouteHandle = { requiresIdentity?: boolean };

// Fires `page_viewed` on every pathname change. Mounted once at the
// app shell. Tracks the matched route id (if set) so events can be
// grouped by logical page even when query/path params differ.
//
// On authenticated routes the first `page_viewed` is deferred until the user
// has been identified in Mixpanel (see `identityReady`). Without this, a cold
// load (hard refresh / bookmarked deep link) commits the app-shell layout —
// and this hook — during React Router's partial-hydration window, before the
// app-shell loader identifies the user, so the event would be attributed to
// the anonymous `$device` id. Unauthenticated routes (`/login`, `/reset-password`,
// `/auth/*`) carry no `requiresIdentity` handle and track immediately.
export function usePageTracking(): void {
  const { pathname } = useLocation();
  const matches = useMatches();
  const { track } = useAnalytics();
  const lastPath = useRef<string | null>(null);

  useEffect(() => {
    if (lastPath.current === pathname) return;

    const matched = matches[matches.length - 1];
    const fire = () => {
      lastPath.current = pathname;
      track('page_viewed', { path: pathname, route_id: matched?.id });
    };

    const requiresIdentity = matches.some(
      (m) => (m.handle as PageTrackingRouteHandle | undefined)?.requiresIdentity,
    );
    if (!requiresIdentity || isIdentityReady()) {
      fire();
      return;
    }

    // Authenticated route whose identity hasn't resolved yet: defer the
    // event. The returned unsubscribe drops the waiter if the route changes
    // before identity resolves (e.g. the loader redirects an unauthenticated
    // visitor to `/login`), so a stale event never fires.
    return onIdentityReady(fire);
  }, [pathname, matches, track]);
}
